import { CreditCard } from 'lucide-react'
import * as React from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { AutopayConsentChoice, type AutopayOffer } from '@/components/AutopayConsentChoice'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { getJSON, postJSON } from '@/lib/api'

interface PaymentLink {
  status: 'payable' | 'nothing_to_pay' | 'unavailable'
  unavailable_reason: string | null
  invoice_number: number
  invoice_status: string
  contact_name: string
  balance: string
  balance_cents: number
  currency: string
  total: string
  subtotal?: string
  total_tax?: string
  tax_total_label?: string
  breakdown?: Array<{ label: string; tax: string }>
  legend?: string | null
  autopay_offer?: AutopayOffer | null
}

interface PaymentLinkResponse {
  payment_link: PaymentLink
}

interface CheckoutResponse {
  checkout_session: {
    checkout_url: string
  }
}

interface ConfirmationResponse extends PaymentLinkResponse {
  confirmation: {
    status: 'pending' | 'payment_received'
  }
}

export function PaymentLinkPage({ token, confirmation }: { token: string; confirmation: boolean }) {
  const [enrollAutopay, setEnrollAutopay] = React.useState(false)
  const query = useQuery({
    queryKey: ['payment-link', token, confirmation],
    queryFn: () => fetchPaymentLink(token, confirmation),
    retry: false,
  })
  const checkout = useMutation({
    mutationFn: () =>
      postJSON<CheckoutResponse>(
        `/api/v1/payment_links/${token}/checkout`,
        enrollAutopay ? { enroll_autopay: true } : {},
      ),
    onSuccess: (data) => {
      window.location.assign(data.checkout_session.checkout_url)
    },
  })
  const paymentLink = query.data?.payment_link
  const confirmationStatus = paymentConfirmationStatus(query.data, confirmation)
  const title = paymentLink ? paymentLinkTitle(paymentLink, confirmationStatus) : ''

  return (
    <main className="min-h-screen bg-background">
      <div className="mx-auto w-full max-w-[1200px] px-4 py-6 sm:px-8">
        <div className="max-w-xl">
          {query.isLoading ? (
            <p className="text-body-sm text-muted-foreground" role="status">
              Loading Payment Link...
            </p>
          ) : query.isError || !paymentLink ? (
            <Alert>Payment Link unavailable</Alert>
          ) : (
            <Card>
              <CardHeader className="p-4 pb-0">
                <CardTitle className="text-heading-sm">{title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 p-4">
                <div className="space-y-1 text-body-sm">
                  <p className="text-foreground">{paymentLink.contact_name}</p>
                  <p className="text-muted-foreground">{paymentLinkDescription(paymentLink)}</p>
                  <PaymentLinkTaxSummary paymentLink={paymentLink} />
                  {paymentLink.status === 'payable' && (
                    <p className="text-heading-sm font-semibold tabular-nums text-foreground">
                      {paymentLink.balance}
                    </p>
                  )}
                  <Button asChild variant="outline" size="sm">
                    <a href={`/pay/${token}/print`} target="_blank" rel="noreferrer">
                      Print / save as PDF
                    </a>
                  </Button>
                </div>
                {paymentLink.status === 'payable' &&
                  confirmationStatus !== 'payment_received' &&
                  paymentLink.autopay_offer && (
                    <AutopayConsentChoice
                      offer={paymentLink.autopay_offer}
                      checked={enrollAutopay}
                      onCheckedChange={setEnrollAutopay}
                    />
                  )}
                {paymentLink.status === 'payable' && confirmationStatus !== 'payment_received' && (
                  <Button
                    type="button"
                    disabled={checkout.isPending}
                    onClick={() => checkout.mutate()}
                  >
                    <CreditCard className="mr-2 h-4 w-4" strokeWidth={1.5} />
                    {checkout.isPending ? 'Opening checkout...' : `Pay ${paymentLink.balance}`}
                  </Button>
                )}
                {checkout.isError && <Alert>Checkout unavailable</Alert>}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </main>
  )
}

function PaymentLinkTaxSummary({ paymentLink }: { paymentLink: PaymentLink }) {
  if (!(paymentLink.breakdown?.length || paymentLink.legend) || !paymentLink.subtotal) {
    return null
  }

  return (
    <div className="mt-3 border-t border-border pt-2 text-body-sm">
      <div className="flex justify-between">
        <span>Subtotal</span>
        <span className="tabular-nums">{paymentLink.subtotal}</span>
      </div>
      {paymentLink.breakdown?.map((row) => (
        <div key={row.label} className="flex justify-between">
          <span>{row.label}</span>
          <span className="tabular-nums">{row.tax}</span>
        </div>
      ))}
      {paymentLink.total_tax && (
        <div className="flex justify-between">
          <span>{paymentLink.tax_total_label ?? 'Total VAT'}</span>
          <span className="tabular-nums">{paymentLink.total_tax}</span>
        </div>
      )}
      <div className="flex justify-between font-semibold">
        <span>Total payable</span>
        <span className="tabular-nums">{paymentLink.total}</span>
      </div>
      {paymentLink.legend && <p className="mt-2 text-muted-foreground">{paymentLink.legend}</p>}
    </div>
  )
}

function fetchPaymentLink(token: string, confirmation: boolean) {
  if (confirmation) {
    return getJSON<ConfirmationResponse>(
      `/api/v1/payment_links/${token}/confirmation${window.location.search}`,
    )
  }

  return getJSON<PaymentLinkResponse>(`/api/v1/payment_links/${token}`)
}

function paymentConfirmationStatus(
  data: PaymentLinkResponse | ConfirmationResponse | undefined,
  confirmation: boolean,
) {
  if (!confirmation) {
    return null
  }

  return (data as ConfirmationResponse | undefined)?.confirmation.status ?? null
}

function paymentLinkTitle(
  paymentLink: PaymentLink,
  confirmationStatus: ConfirmationResponse['confirmation']['status'] | null,
) {
  if (confirmationStatus === 'payment_received') {
    return 'Payment received'
  }
  if (paymentLink.status === 'nothing_to_pay') {
    return 'Nothing to pay'
  }
  if (paymentLink.status === 'unavailable') {
    return 'Payment unavailable'
  }

  return `Invoice #${paymentLink.invoice_number}`
}

function paymentLinkDescription(paymentLink: PaymentLink) {
  if (paymentLink.status === 'nothing_to_pay') {
    return `Invoice #${paymentLink.invoice_number} has no open Balance.`
  }
  if (paymentLink.status === 'unavailable') {
    return paymentLink.unavailable_reason ?? 'Online payment is unavailable.'
  }

  return 'Open Balance'
}
